/* eslint-disable react/prop-types */
import React from 'react';
import BreadcrumbsContainer from '../components/Breadcrumbs';
import {
  Grid,
  GridProps,
  Flex,
  FlexProps,
  Heading,
  Text,
  Box,
  BoxProps,
} from '@chakra-ui/react';
import { useLayoutScroll } from 'hooks/useLayoutScroll';

interface LayoutProps extends GridProps {
  children: React.ReactNode;
}

interface ChildrenProps {
  children: React.ReactNode;
}

interface HeaderProps {
  children: React.ReactNode;
  text?: number;
}

const Layout = ({ children, ...props }: LayoutProps): JSX.Element => {
  const { overflow, marginRight } = useLayoutScroll();

  return (
    <Grid
      templateColumns='1fr 1fr'
      templateRows='auto auto auto auto auto 1fr'
      templateAreas={`
      "breadcrumbs breadcrumbs"
      "header actions"
      "subheader actions"
      "client status"
      "date status"
      "body body"
    `}
      gap={2}
      px={6}
      h='100%'
      overflow={overflow}
      mr={marginRight}
      {...props}
    >
      {children}
    </Grid>
  );
};

const Breadcrumbs = ({ children }: ChildrenProps): JSX.Element => (
  <BreadcrumbsContainer gridArea='breadcrumbs'>{children}</BreadcrumbsContainer>
);

const Header = ({ text, children }: HeaderProps): JSX.Element => (
  <>
    <Heading
      as='h2'
      size='lg'
      gridArea='header'
      fontWeight='bold'
      position='sticky'
    >
      <Flex gap={2} alignItems='center'>
        {children}
        {text ? (
          <Text fontSize='lg' fontWeight='bold' color='gray.500'>
            ({text})
          </Text>
        ) : null}
      </Flex>
    </Heading>
  </>
);

const Subheader = ({ children, ...props }: HeaderProps): JSX.Element => (
  <Heading as='h3' size='md' gridArea='subheader' fontWeight='bold' {...props}>
    {children}
  </Heading>
);

const Actions = ({ children, ...props }: FlexProps): JSX.Element => (
  <Flex justifyContent='flex-end' gridGap={2} gridArea='actions' {...props}>
    {children}
  </Flex>
);

const Body = ({ children, ...props }: GridProps): JSX.Element => (
  <Grid gridArea='body' gridGap={6} gridAutoRows='min-content' {...props}>
    {children}
  </Grid>
);

const Date = ({ children }: ChildrenProps): JSX.Element => (
  <Text gridArea='date' textAlign='left'>
    {children}
  </Text>
);

const Client = ({ children, ...props }: BoxProps): JSX.Element => (
  <Box as='div' gridArea='client' {...props}>
    {children}
  </Box>
);

const Status = ({ children, ...props }: FlexProps): JSX.Element => (
  <Flex
    gridArea='status'
    justifyContent='flex-end'
    alignItems='flex-end'
    flexFlow='row'
    gap={2}
    {...props}
  >
    {children}
  </Flex>
);

Layout.Breadcrumbs = Breadcrumbs;
Layout.Header = Header;
Layout.Subheader = Subheader;
Layout.Actions = Actions;
Layout.Body = Body;
Layout.Date = Date;
Layout.Client = Client;
Layout.Status = Status;

export { Breadcrumbs, Header, Subheader, Actions, Body, Date, Client, Status };
export default Layout;
